import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { ReferenceTableService } from './reference-table.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-reference-table',
    templateUrl: './reference-table.component.html',
    styleUrls: ['./reference-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReferenceTableComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  minimise: boolean = false;

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};

  // 🔴 GLOBAL SERVICES AS @INPUT - ACCESS VIA @INPUT, NOT NEW SERVICES
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  // Standardized component properties
  currentTabName: string = 'Form Entry';
  isSaveClicked: boolean = false;
  crudLoading: boolean = false;
  modalTitle: string = 'Entity Reference Table';
  showFormLoader: boolean = true;
  // Per-action loading flags
  isSavingUpdate: boolean = false;
  isDeleting: boolean = false;

  // Entity table
  entityList: Array<{ key: string; value: any }> = [];

  // Form data
  referenceTableEntity: any = {};
  referenceTableEntity_bckup: any = {};
  isEdit: boolean = false;

  // Form group for reactive forms
  referenceTableForm: UntypedFormGroup = new UntypedFormGroup({});

  // Year picker states
  showFromYearPicker: boolean = false;
  showToYearPicker: boolean = false;
  fromTaxYear: number | null = null;
  toTaxYear: number | null = null;

  // API completion flags
  referenceTableApiCompleted = false;

  constructor(
    private service: ReferenceTableService,
    private gtwSnackbarService: GtwSnackbarService,
    private fb: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  /**
   * Initialize reactive form
   */
  private initializeForm(): void {
    this.referenceTableForm = this.fb.group({
      TABLE_NAME: ['', Validators.required],
      FROM_TAX_YEAR: ['', Validators.required],
      TO_TAX_YEAR: ['', Validators.required],
      DESCRIPTION: [''],
      IS_ACTIVE: ['Y', Validators.required],
      REF_CODE: [''],
      REF_VALUE: ['']
    });
  }

  init(): void {
    // Only proceed if we have all required data
    if (this.GENERAL_CONFIG && 
        this.GENERAL_CONFIG.base_url &&
        this.filterParams && 
        Object.keys(this.filterParams).length > 0) {
      this.loadInitialData();
    }
  }

  /**
   * Load initial data
   */
  private loadInitialData(): void {
    if (!this.GENERAL_CONFIG?.base_url || !this.globalService?.globalParams || !this.filterParams) {
      return;
    }

    this.showFormLoader = true;
    this.setupEntityData();
    this.initializeComponent();
  }

  /**
   * Check if all API calls are completed
   */
  private checkLoadingCompletion(): void {
    if (this.referenceTableApiCompleted) {
      this.showFormLoader = false;
    }
  }

  private setupEntityData(): void {
    // CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.entityList = [
      { key: 'Tax Year', value: this.rowData?.TAX_YEAR || this.filterParams?.tax_year || '' },
      { key: 'LEID', value: this.rowData?.LEID || '' },
      { key: 'LE Name', value: this.rowData?.LE_NAME || '' }
    ];
  }

  private initializeComponent(): void {
    // Determine if this is edit mode based on rowData
    this.isEdit = this.rowData && Object.keys(this.rowData).length > 0;
    
    if (this.isEdit) {
      this.referenceTableEntity_bckup = { ...this.rowData };
      // 🚀 CRITICAL: Map column_name to TABLE_NAME if available
      if (this.rowData.COLUMN_NAME || this.rowData.column_name) {
        this.referenceTableEntity_bckup.TABLE_NAME = this.rowData.COLUMN_NAME || this.rowData.column_name;
      }
      
      // 🚀 CRITICAL: AngularJS uses FROM_YEAR/TO_YEAR, but component uses FROM_TAX_YEAR/TO_TAX_YEAR
      // Check both field name variants to ensure compatibility
      const fromYearValue = this.rowData.FROM_YEAR || this.rowData.FROM_TAX_YEAR;
      const toYearValue = this.rowData.TO_YEAR || this.rowData.TO_TAX_YEAR;
      
      console.log('Debug - rowData years:', {
        FROM_YEAR: this.rowData.FROM_YEAR,
        FROM_TAX_YEAR: this.rowData.FROM_TAX_YEAR,
        TO_YEAR: this.rowData.TO_YEAR,
        TO_TAX_YEAR: this.rowData.TO_TAX_YEAR,
        fromYearValue,
        toYearValue
      });
      
      if (fromYearValue !== undefined && fromYearValue !== null) {
        const normalizedFrom = this.normalizeYear(fromYearValue);
        if (normalizedFrom) {
          this.fromTaxYear = normalizedFrom;
          this.referenceTableEntity_bckup.FROM_TAX_YEAR = normalizedFrom.toString();
          console.log('Debug - set fromTaxYear (normalized):', normalizedFrom);
        }
      }
      
      if (toYearValue !== undefined && toYearValue !== null) {
        const normalizedTo = this.normalizeYear(toYearValue);
        if (normalizedTo) {
          this.toTaxYear = normalizedTo;
          this.referenceTableEntity_bckup.TO_TAX_YEAR = normalizedTo.toString();
          console.log('Debug - set toTaxYear (normalized):', normalizedTo);
        }
      }
    } else {
      // Create mode - use column_name if available from rowData or filterParams
      const columnName = this.rowData?.COLUMN_NAME || this.rowData?.column_name || this.filterParams?.column_name || '';
      
      this.referenceTableEntity_bckup = {
        TABLE_NAME: columnName,
        FROM_TAX_YEAR: this.filterParams?.tax_year || '',
        TO_TAX_YEAR: '9999',
        DESCRIPTION: '',
        IS_ACTIVE: 'Y',
        REF_CODE: '',
        REF_VALUE: ''
      };
      this.fromTaxYear = this.filterParams?.tax_year ? parseInt(this.filterParams.tax_year) : null;
      this.toTaxYear = 9999;
    }
    
    this.referenceTableEntity = { ...this.referenceTableEntity_bckup };
    
    // 🚀 CRITICAL: Ensure year values are properly set in the entity
    if (this.fromTaxYear) {
      this.referenceTableEntity.FROM_TAX_YEAR = this.fromTaxYear.toString();
    }
    if (this.toTaxYear) {
      this.referenceTableEntity.TO_TAX_YEAR = this.toTaxYear.toString();
    }
    
    this.updateFormValues();
    this.loadReferenceTableData();
  }

  /**
   * Normalize a year-like value into a 4-digit year.
   * If the source is an ISO date ending on 12/31 (e.g. 2016-12-31T18:30:00.000Z), bump to next year (2017).
   */
  private normalizeYear(value: any): number | null {
    try {
      if (value instanceof Date) {
        const y = value.getUTCFullYear();
        const m = value.getUTCMonth(); // 0-11
        const d = value.getUTCDate();
        if (m === 11 && d === 31) { // Dec 31 → treat as next tax year
          return y + 1;
        }
        return y;
      }
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        // If ISO string contains -12-31, bump year by 1
        const yearMatch = trimmed.match(/(\d{4})/);
        const yr = yearMatch ? parseInt(yearMatch[1], 10) : NaN;
        if (!isNaN(yr)) {
          if (/\-12\-31/i.test(trimmed)) {
            return yr + 1;
          }
          return yr;
        }
      }
    } catch (e) {
      // no-op
    }
    return null;
  }

  /**
   * Update form values from entity data
   */
  private updateFormValues(): void {
    console.log('Debug - updateFormValues:', {
      fromTaxYear: this.fromTaxYear,
      toTaxYear: this.toTaxYear,
      referenceTableEntity: this.referenceTableEntity
    });
    
    // 🚀 CRITICAL: Update form controls with current year picker values
    this.referenceTableForm.patchValue({
      TABLE_NAME: this.referenceTableEntity.TABLE_NAME || '',
      FROM_TAX_YEAR: this.fromTaxYear?.toString() || this.referenceTableEntity.FROM_TAX_YEAR || '',
      TO_TAX_YEAR: this.toTaxYear?.toString() || this.referenceTableEntity.TO_TAX_YEAR || '',
      DESCRIPTION: this.referenceTableEntity.DESCRIPTION || '',
      IS_ACTIVE: this.referenceTableEntity.IS_ACTIVE || 'Y',
      REF_CODE: this.referenceTableEntity.REF_CODE || '',
      REF_VALUE: this.referenceTableEntity.REF_VALUE || ''
    });
    
    console.log('Debug - form values after patch:', this.referenceTableForm.value);
  }

  loadReferenceTableData(): void {
    if (!this.GENERAL_CONFIG?.base_url || !this.globalService?.globalParams || !this.filterParams) {
      return;
    }
    
    this.service.loadReferenceTableData(
      this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '',
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response?.jsonObject) {
          // Handle reference table data loading
          this.referenceTableApiCompleted = true;
          this.checkLoadingCompletion();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error loading reference table data:', error);
        this.referenceTableApiCompleted = true;
        this.checkLoadingCompletion();
        this.cdr.markForCheck();
      }
    );
  }

  // Year picker methods
  openFromYearPicker(): void {
    this.showFromYearPicker = true;
  }

  closeFromYearPicker(): void {
    this.showFromYearPicker = false;
  }

  onFromYearSelected(year: number | null): void {
    if (year === null) {
      // 🚀 CRITICAL: Handle clear action - clear the year
      this.fromTaxYear = null;
      this.referenceTableForm.patchValue({ FROM_TAX_YEAR: '' });
      this.referenceTableEntity.FROM_TAX_YEAR = '';
    } else {
      // Set the year
      this.fromTaxYear = year;
      this.referenceTableForm.patchValue({ FROM_TAX_YEAR: year.toString() });
      this.referenceTableEntity.FROM_TAX_YEAR = year.toString();
    }
    this.closeFromYearPicker();
  }

  openToYearPicker(): void {
    this.showToYearPicker = true;
  }

  closeToYearPicker(): void {
    this.showToYearPicker = false;
  }

  onToYearSelected(year: number | null): void {
    if (year === null) {
      // 🚀 CRITICAL: Handle clear action - clear the year
      this.toTaxYear = null;
      this.referenceTableForm.patchValue({ TO_TAX_YEAR: '' });
      this.referenceTableEntity.TO_TAX_YEAR = '';
    } else {
      // Set the year
      this.toTaxYear = year;
      this.referenceTableForm.patchValue({ TO_TAX_YEAR: year.toString() });
      this.referenceTableEntity.TO_TAX_YEAR = year.toString();
    }
    this.closeToYearPicker();
  }

  trackByIndex(index: number, item: any): number {
    return index;
  }

  isValid(): boolean {
    // Clear previous validation errors
    this.referenceTableEntity.TABLE_NAME_NOT_VALID = false;
    this.referenceTableEntity.FROM_TAX_YEAR_NOT_VALID = false;
    this.referenceTableEntity.TO_TAX_YEAR_NOT_VALID = false;
    this.referenceTableEntity.IS_ACTIVE_NOT_VALID = false;

    if (!this.referenceTableEntity.TABLE_NAME) {
      this.referenceTableEntity.TABLE_NAME_NOT_VALID = true;
      return false;
    }
    // 🚀 CRITICAL: Check both entity and year picker values for validation
    const fromYear = this.referenceTableEntity.FROM_TAX_YEAR || this.fromTaxYear?.toString();
    const toYear = this.referenceTableEntity.TO_TAX_YEAR || this.toTaxYear?.toString();
    
    if (!fromYear) {
      this.referenceTableEntity.FROM_TAX_YEAR_NOT_VALID = true;
      return false;
    }
    if (!toYear) {
      this.referenceTableEntity.TO_TAX_YEAR_NOT_VALID = true;
      return false;
    }
    // if (!this.referenceTableEntity.IS_ACTIVE) {
    //   this.referenceTableEntity.IS_ACTIVE_NOT_VALID = true;
    //   return false;
    // }
    return true;
  }

  save(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }

    this.isSaveClicked = true;
    this.isSavingUpdate = true;

    const saveData = this.buildSaveData();
    const settingsObj = this.buildSettings();

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';

    this.service.saveReferenceTableForm(
      baseUrl,
      saveData,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData,
      settingsObj
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.isSavingUpdate = false;

        if (response?.callSuccess === "1" && response?.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Reference Table Details Updated successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Error occurred while saving', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.isSavingUpdate = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  delete(): void {
    this.isSaveClicked = true;
    this.isDeleting = true;

    const saveData = this.buildSaveData();
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';

    this.service.deleteReferenceTableForm(
      baseUrl,
      saveData,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.isDeleting = false;

        if (response?.callSuccess === "1" && response?.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response?.errorMessage || 'Error occurred while deleting', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.isDeleting = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  reset(): void {
    this.referenceTableEntity = { ...this.referenceTableEntity_bckup };
    
    // 🚀 CRITICAL: Reset year picker values from backup
    if (this.referenceTableEntity_bckup.FROM_TAX_YEAR) {
      const fromYearValue = this.referenceTableEntity_bckup.FROM_TAX_YEAR;
      const normalizedFrom = this.normalizeYear(fromYearValue);
      if (normalizedFrom !== null) {
        this.fromTaxYear = normalizedFrom;
      } else {
        // Fallback: try parsing as number
        const parsed = parseInt(fromYearValue.toString(), 10);
        this.fromTaxYear = isNaN(parsed) ? null : parsed;
      }
    } else {
      this.fromTaxYear = null;
    }
    
    if (this.referenceTableEntity_bckup.TO_TAX_YEAR) {
      const toYearValue = this.referenceTableEntity_bckup.TO_TAX_YEAR;
      const normalizedTo = this.normalizeYear(toYearValue);
      if (normalizedTo !== null) {
        this.toTaxYear = normalizedTo;
      } else {
        // Fallback: try parsing as number
        const parsed = parseInt(toYearValue.toString(), 10);
        this.toTaxYear = isNaN(parsed) ? null : parsed;
      }
    } else {
      this.toTaxYear = null;
    }
    
    this.updateFormValues();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private buildSettings(): any {
    return {
      tax_year: this.filterParams?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.filterParams?.jcd_key || this.rowData?.JCD_KEY,
      combination_key: this.filterParams?.combination_key || this.rowData?.COMBINATION_KEY,
      trans_type_key: this.filterParams?.trans_type_key || this.rowData?.TRANS_TYPE_KEY,
      scenario_code: this.filterParams?.scenario_code || this.rowData?.SCENARIO_CODE || this.globalService?.globalParams?.scenario_code,
      is_issue_key_enabled: 'N'
    };
  }

  private buildSaveData(): any {
    const saveData = { ...this.referenceTableEntity };
    
    // Set action based on edit mode
    if (this.isEdit) {
      saveData.ACTION = 'UPDATE';
    } else {
      saveData.ACTION = 'INSERT';
    }

    // Add required parameters from filterParams and globalService
    saveData.TAX_YEAR = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    saveData.SCENARIO = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    saveData.JCD_KEY = this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key;
    saveData.JCD_TA_KEY = this.filterParams?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key;
    saveData.ACTIVITY_KEY = this.filterParams?.activity_key || this.globalService?.globalParams?.activity_key;
    saveData.SCREEN_KEY = this.filterParams?.screen_key || this.globalService?.globalParams?.screen_key;
    saveData.SCREEN_TYPE = this.filterParams?.screen_type || this.globalService?.globalParams?.screen_type || 'grid';
    saveData.PROCESS_NAME = this.filterParams?.process_name || this.globalService?.globalParams?.process_name;
    saveData.FILING_KEY = this.filterParams?.filing_key || this.globalService?.globalParams?.filing_key || this.rowData?.CONSOL_GROUP_KEY;

    // Add dynamic issue-related fields
    saveData.IS_LOCKED = this.filterParams?.is_locked || this.globalService?.globalParams?.is_locked || 'N';
    saveData.ISSUE_ID = this.filterParams?.issue_id || this.globalService?.globalParams?.issue_id || this.rowData?.issue_id || '';
    saveData.ISSUE_SHORT_DESC = this.filterParams?.issue_short_desc || this.globalService?.globalParams?.issue_short_desc || this.rowData?.issue_short_desc || '';
    saveData.ISSUE_ID_FOR_TX_SAVE = this.filterParams?.issue_id_for_tx_save || this.globalService?.globalParams?.issue_id_for_tx_save || this.rowData?.issue_id_for_tx_save || '';
    saveData.IS_ISSUE_KEY_ENABLED = this.filterParams?.is_issue_key_enabled || this.globalService?.globalParams?.is_issue_key_enabled || 'N';
    saveData.SCENARIO_CODE = this.filterParams?.scenarioCode || this.globalService?.globalParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.globalParams?.scenario_code || 'RAF';

    // Add reference table specific fields
    saveData.TABLE_NAME = this.referenceTableEntity.TABLE_NAME || '';
    saveData.FROM_TAX_YEAR = this.referenceTableEntity.FROM_TAX_YEAR || this.fromTaxYear?.toString() || '';
    saveData.TO_TAX_YEAR = this.referenceTableEntity.TO_TAX_YEAR || this.toTaxYear?.toString() || '';
    saveData.REF_CODE = this.referenceTableEntity.REF_CODE || '';
    saveData.REF_VALUE = this.referenceTableEntity.REF_VALUE || '';
    
    // Add object_id for update operations
    saveData.object_id = this.rowData?.object_id || '';
    
    // Add LAST_UPDATED fields for update operations
    if (this.isEdit) {
      saveData.LAST_UPDATED_BY = this.USER_SETTINGS?.user?.sso_id || this.globalService?.globalParams?.sso_id || '';
      saveData.LAST_UPDATED_DATE = this.rowData?.LAST_UPDATED_DATE || null;
      saveData.SCENARIO_TYPE_CODE = this.rowData?.SCENARIO_TYPE_CODE || '';
    }

    return saveData;
  }
}

